// Copyright (c) Docugami, Inc. All rights reserved.

import { nanoid } from "@reduxjs/toolkit";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { AddBindingInput, addBindingThunk, AddEntryInput, addEntryThunk, bulkConfirmBindings, publishReportThunk, RemoveBindingInput, removeBindingThunk } from "components/reports/reportsActions";
import { QueryData } from "components/reports/ReportsContext";
import { BindingProviderId, GetReportBindingStatsResponse, ReportSettings, ReportViewContext } from "networking/reportsApi";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useGetFeedbackStatusQuery } from "store/frontend/frontendSlice";
import { OperationMetadata, selectGetReviewBindingStatsState, setReportLabels, useBindEntryMutation, useGetBindingsQuery, useGetPublishedDocumentsQuery, useGetReportSchemaQuery, useGetReviewBindingStatsQuery, useGetReviewDocumentsQuery, usePublishReportMutation } from "store/reports/reportsSlice";
import { selectEntries, selectReportContext, selectReportDocumentsView, selectReportEntryBindings, selectReportId } from "store/reports/selectors";
import { ConfirmAllDocumentBindingsSchema, ConfirmAllEntryBindingsSchema, ConfirmEntryBindingSchema, TelemetryEvent } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";
import { useAppDispatch, useAppSelector } from "./redux";
import { useReportOperationByMetadataStatus } from "./reportOperationStatus";
import { folderIdSelector } from "store/dgml/dgmlSelectors";

interface ReportsContextProviderProps {
    clusterId: string;
    reportId: string;
    reportTitleLabel: string;
    reportItemLabel: string;
}

export const useReportsContextProvider = (props: ReportsContextProviderProps): void => {
    const context = useAppSelector(selectReportContext);
    const { clusterId, reportId } = props;
    const { data: schema } = useGetReportSchemaQuery(reportId ?? skipToken);
    const currentFileId = useAppSelector(state => state.reportsSlice.fileId);
    const currentDocsetId = useAppSelector(state => state.reportsSlice.clusterId);

    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(setReportLabels({ itemsName: props.reportItemLabel, titleName: props.reportTitleLabel }))
    }, [dispatch, props.reportItemLabel, props.reportTitleLabel]);

    const isBindingsQueryEnabled = !!reportId && !!context && !!schema;
    useGetReviewDocumentsQuery(isBindingsQueryEnabled ? reportId : skipToken);

    const isReviewQueryEnabled = !!schema && (!schema.report.isPublished || !schema.report.autoPublishDocuments);
    useGetReviewDocumentsQuery(isReviewQueryEnabled ? reportId : skipToken);

    const isPublishedQueryEnabled = !!schema?.report.isPublished && context === ReportViewContext.Published;
    useGetPublishedDocumentsQuery(isPublishedQueryEnabled ? reportId : skipToken);

    useGetFeedbackStatusQuery({ docsetId: clusterId });

    const isDocumentValid = !!currentFileId && !!schema && currentDocsetId === schema.report.clusterId && reportId === schema.report.id;
    useGetBindingsQuery(isDocumentValid ? { reportId, fileId: currentFileId } : skipToken);
}

export const useGetReviewBindingStats = (reportId: string): QueryData<GetReportBindingStatsResponse> => {
    const state = useAppSelector(state => selectGetReviewBindingStatsState(state, reportId));
    const { isLoading, error, isFetching, refetch } = useGetReviewBindingStatsQuery(reportId, {
        skip: !reportId,
    });

    return {
        isLoading,
        isFetching,
        data: state,
        error,
        refetch
    }
}

export const useAddEntry = (reportId: string) => {
    const cacheKey = useRef(nanoid());
    const [, data] = useBindEntryMutation();

    const dispatch = useAppDispatch();
    const trigger = useCallback(async (input: AddEntryInput) => {
        await dispatch(addEntryThunk(reportId, input, cacheKey.current));
    }, [dispatch, reportId]);

    return {
        trigger,
        data
    };
}

export const useAddBinding = (reportId: string) => {
    const cacheKey = useRef(nanoid());
    const [, data] = useBindEntryMutation({
        fixedCacheKey: cacheKey.current
    });

    const dispatch = useAppDispatch();
    const trigger = useCallback(async (input: AddBindingInput) => {
        Telemetry.trackEvent<ConfirmEntryBindingSchema>({
            eventName: TelemetryEvent.ConfirmEntryBinding,
            reportId: reportId,
            documentId: input.folderId,
            entryId: input.entryId,
            instanceId: input.instanceId ?? ''
        });

        await dispatch(addBindingThunk(reportId, input, cacheKey.current));
    }, [dispatch, reportId]);

    return {
        trigger,
        data
    };
}

export const useRemoveBinding = (reportId: string) => {
    const cacheKey = useRef(nanoid());
    const [, data] = useBindEntryMutation({
        fixedCacheKey: cacheKey.current
    });

    const dispatch = useAppDispatch()
    const trigger = useCallback(async (input: RemoveBindingInput) => {
        await dispatch(removeBindingThunk(reportId, input, cacheKey.current));
    }, [reportId, dispatch]);

    return {
        trigger,
        data
    }
}

interface PublishReportInput {
    settings: ReportSettings;
    operationId?: string;
}

export const usePublishReport = (reportId: string) => {
    const cacheKey = useRef(nanoid());
    const [, data] = usePublishReportMutation({
        fixedCacheKey: cacheKey.current
    });

    const dispatch = useAppDispatch()
    const trigger = useCallback(async (input: PublishReportInput) => {
        await dispatch(publishReportThunk(reportId, input.settings, cacheKey.current, input.operationId));
    }, [reportId, dispatch]);

    return {
        trigger,
        data
    }
}

export const useAllBindingsForEntryConfirmed = (entryId: string): boolean | undefined => {
    const context = useAppSelector(selectReportContext);
    const reviewStats = useAppSelector(state => selectGetReviewBindingStatsState(state, state.reportsSlice.reportId));
    const documentIds = useAppSelector(selectReportDocumentsView)?.ids;

    return useMemo(() => {
        if (context === ReportViewContext.Published) {
            return true;
        }

        if (!reviewStats?.documentEntries || !documentIds?.length) {
            return false;
        }

        for (const documentId of documentIds) {
            if (!reviewStats.documentEntries[documentId]?.[entryId]) {
                return false
            }
        }

        return true;
    }, [reviewStats, entryId, context, documentIds]);
}

export const useEntryBindingsForDocumentConfirmed = (folderId: string, entryIds: string[]): boolean => {
    const context = useAppSelector(selectReportContext);
    const hasCpml = useAppSelector(state => !!state.documents.indexed[folderId]?.clusterDocument.hasCpml);
    const reviewStats = useAppSelector(state => selectGetReviewBindingStatsState(state, state.reportsSlice.reportId));

    return useMemo(() => {
        if (context === ReportViewContext.Published) {
            return true;
        }

        if (!entryIds.length || !hasCpml || !reviewStats) {
            return false;
        }

        const entryStats = reviewStats.documentEntries[folderId];
        if (!entryStats) {
            return false;
        }

        return entryIds.every(entryId => entryStats[entryId])
    }, [context, reviewStats, folderId, entryIds, hasCpml]);
}

export const useAllBindingsForDocumentAreForProvider = (folderId: string, providerId: BindingProviderId): boolean => {
    const { fileId } = folderIdSelector(folderId)!;
    const entryBindings = useAppSelector(state => selectReportEntryBindings(state, fileId));
    const entries = useAppSelector(selectEntries);

    return useMemo(() => {
        if (!!entries && !!entryBindings) {
            for (const entryId of entries.ids) {
                const bindings = entryBindings.entities[entryId]?.bindings;
                if (!bindings?.length || !!bindings.find(x => x.bindingProviderId !== providerId)) {
                    return false;
                }
            }

            return true;
        }

        return false;
    }, [entryBindings, entries, providerId]);
}

export interface ConfirmAllBindings {
    confirming: boolean;
    confirm: () => void;
    isDisabled: boolean;
}

export const useConfirmDocumentBindings = (folderId: string, entryIds: string[]): ConfirmAllBindings => {
    const hasCpml = useAppSelector(state => !!state.documents.indexed[folderId]?.clusterDocument.hasCpml);
    const reportId = useAppSelector(selectReportId)!;

    const metadataPredicate = useCallback((metadata: OperationMetadata) => !!metadata?.folderId && metadata.folderId === folderId, [folderId])
    const confirming = useReportOperationByMetadataStatus(metadataPredicate);
    const dispatch = useAppDispatch();

    const confirm = async () => {
        if (confirming || !hasCpml) {
            return null;
        }

        Telemetry.trackEvent<ConfirmAllDocumentBindingsSchema>({
            reportId: reportId,
            folderId: folderId,
            eventName: TelemetryEvent.ConfirmAllDocumentBindings
        });

        await dispatch(bulkConfirmBindings([folderId], entryIds, { folderId }))
    }

    return {
        confirming: confirming,
        confirm: confirm,
        isDisabled: !hasCpml
    }
}

export const useConfirmAllEntryBindings = (entryId: string): ConfirmAllBindings => {
    const documentsView = useAppSelector(selectReportDocumentsView);
    const reportId = useAppSelector(selectReportId)!;

    const metadataPredicate = useCallback((metadata: OperationMetadata) => metadata.entryId === entryId, [entryId])
    const confirming = useReportOperationByMetadataStatus(metadataPredicate);
    const dispatch = useAppDispatch();

    const docsWithDgml = useMemo(() => {
        if (!documentsView?.ids.length) {
            return [];
        }

        const docs: string[] = [];
        for (const id of documentsView.ids) {
            const doc = documentsView.entities[id];
            if (!!doc?.clusterDocument.hasCpml) {
                docs.push(doc.folderId);
            }
        }

        return docs;
    }, [documentsView]);

    const confirm = async () => {
        if (confirming || docsWithDgml.length === 0) {
            return;
        }

        Telemetry.trackEvent<ConfirmAllEntryBindingsSchema>(
            {
                reportId: reportId,
                entryId: entryId,
                documentIds: docsWithDgml,
                eventName: TelemetryEvent.ConfirmAllEntryBindings
            });

        await dispatch(bulkConfirmBindings(docsWithDgml, [entryId], { entryId }));
    }

    return {
        confirming: confirming,
        confirm: confirm,
        isDisabled: docsWithDgml.length === 0
    }
}
